import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { FaUserTie } from 'react-icons/fa';
import { FaUserFriends } from 'react-icons/fa';
import { FiEdit, FiMail, FiPlus, FiTrash, FiEye } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  IconButton,
  InputGroup,
  Stack,
  Tooltip,
  useDisclosure,
  useToast,
  Alert,
  AlertIcon,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import queryString from 'query-string';
import * as Yup from 'yup';

import { Replace } from '~/helpers/Replace';
import { Demand } from '~/modules/activityExecution/@types/activity';
import BadgeStatusInfo from '~/modules/dashboard/components/BadgeStatusInfo';
import AlertDialog from '~/shared/components/AlertDialog';
import Input from '~/shared/components/InputChakra';
import CheckboxInput from '~/shared/components/InputChakra/CheckboxChakra';
import SelectChakra from '~/shared/components/InputChakra/SelectChakra';
import TextareaChakra from '~/shared/components/InputChakra/Textarea';
import Loading from '~/shared/components/Loading';
import ModalChakra from '~/shared/components/Modal/ChakraUI';
import { IPaginationProps } from '~/shared/components/Pagination';
import SectionHeader from '~/shared/components/SectionHeader';
import Table, { THeadProps } from '~/shared/components/Table';
import api from '~/shared/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import { Department } from '../../@types/management';
import { Container } from './styles';

interface DemandList extends Demand {
  departments_names?: string;
}

export interface DemandProps extends IPaginationProps {
  data: DemandList[];
}

interface DepartmentOption extends Replace<Department, { id: string }> {
  value: string;
  label: string;
}

interface StatusSelect {
  value: number;
  label: string;
}
interface PermissionsSelect {
  value: number;
  label: string;
}

interface QueryParams {
  id?: string;
  pg?: string;
}

const DemandActivities: React.FC = () => {
  const statusDem: StatusSelect[] = [
    { value: 8, label: 'Ativa' },
    { value: 9, label: 'Inativa' },
  ];

  const permissionsDem: PermissionsSelect[] = [
    { value: 1, label: 'Permissão de planejamento pelos lideres' },
    { value: 2, label: 'Permissão de planejamento pelos funcionários' },
  ];

  const history = useHistory();
  const location = useLocation();

  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const formRef = useRef<FormHandles>(null);
  const formRefEdit = useRef<FormHandles>(null);

  const cancelDeleteRef = useRef(null);
  const btnCreateRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const [toggleDepartment, setToggleDepartment] = useState(false);
  const [toggleEditDepartment, setTogglEditDepartment] = useState(false);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [demandEdit, setDemandEdit] = useState<Demand | null>({} as Demand);

  const [demandDelete, setDemandDelete] = useState(0);

  const [modalSuccess, setModalSuccess] = useState(0);

  const [demand, setDemand] = useState<DemandProps>({} as DemandProps);

  const queryParams = useMemo<QueryParams>(() => {
    return queryString.parse(location.search);
  }, [location.search]);

  const [page, setPage] = useState(
    queryParams?.pg ? Number(queryParams?.pg) : 1,
  );

  /* const handleGetParams = useCallback((): string => {
    const paramsInfoData = {
      ...queryParams,
    };

    return queryString.stringify(paramsInfoData);
  }, [queryParams]); */

  /* const [steps, setSteps] = useState(1); */

  const addToast = useToast();

  const tableTitles = useMemo((): THeadProps[] => {
    return [
      {
        title: 'Demanda',
      },
      {
        title: 'Duração',
        help:
          'Representa a duração total das atividades cadastradas na demanda',
      },
      {
        title: 'Descrição',
      },
      {
        title: 'Departamento(s)',
      },
      {
        title: 'Permissão',
      },
      {
        title: 'Status',
      },
      {
        title: 'Ações',
      },
    ];
  }, []);

  const getDepartments = useCallback(async (): Promise<void> => {
    const response = await api.get<Department[]>('department', {
      params: {
        only_active: 1,
      },
    });

    const { data } = response;

    if (!data.length) return;

    setDepartments(
      data.map((dep) => {
        return {
          ...dep,
          label: dep.name,
          value: dep.id.toString(),
          id: dep.id.toString(),
        };
      }),
    );

    /* Não podemos utilizar o estado page aqui porque ao atualizar a página essa callback seria chamada novamente e como
    está no array de dependencias do useEffect o executaria também pois a sua dependência mudou de valor */
  }, []);

  const handleFormatDepartment = useCallback((dem: DemandList) => {
    let departmentsNames = '';

    dem.departments.forEach((dep: any, i: number) => {
      if (i > 0 && i < dem.departments?.length) departmentsNames += ' | ';

      departmentsNames += dep.name;
    });

    return departmentsNames;
  }, []);

  useEffect(() => {
    setLoaded(false);

    getDepartments();

    api
      .get(`demand`, {
        params: { page, include_departments: 1 },
      })
      .then((response) => {
        const demandD = response.data;

        setDemand({
          ...demandD,
          data: demandD.data.map((dem: any) => {
            let departmentsNames = '';

            if (dem?.departments?.length) {
              departmentsNames = handleFormatDepartment(dem);
            }

            const demandFormatted: DemandProps = {
              ...dem,
              departments_names: departmentsNames,
            };

            return demandFormatted;
          }),
        });
      })

      .finally(() => setLoaded(true));
  }, [page, getDepartments, handleFormatDepartment]);

  const handleDemandActivitiesList = (demandId: number): void => {
    history.push({
      pathname: `demandas/${demandId}`,
      search: `pg=${page}`,
    });
  };

  const handleEditDemand = useCallback(
    (data: Demand) => {
      setDemandEdit(data);
      setTogglEditDepartment(!!data?.departments?.length);
      onOpenEdit();
    },
    [onOpenEdit],
  );

  const handleEditDemandForm = useCallback(
    async (data: Demand) => {
      setLoadingEdit(true);
      try {
        formRefEdit.current?.setErrors({});

        const validation = Yup.object().shape({
          name: Yup.string().required('Nome da demanda é obrigatório'),
          status_id: Yup.number().required('Status é obrigatório'),
        });
        await validation.validate(data, {
          abortEarly: false,
        });

        const response = await api.put<Demand>(
          `demand/${demandEdit?.id}`,
          data,
        );

        const { data: newDemand } = response;

        setDemand((state) => {
          return {
            ...state,
            data: [
              ...state.data.map((dataDemand) => {
                return dataDemand.id === demandEdit?.id
                  ? {
                      ...newDemand,
                      total_duration_activities:
                        dataDemand.total_duration_activities,
                      departments_names: handleFormatDepartment(newDemand),
                    }
                  : dataDemand;
              }),
            ],
          };
        });

        formRefEdit.current?.reset();

        addToast({
          position: 'top-right',
          status: 'success',
          title: 'Demanda atualizada!',
          description: 'A Demanda foi atualizada com sucesso.',
          isClosable: true,
        });

        onCloseEdit();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRefEdit.current?.setErrors(errors);
          return;
        }
        const { error } = err.response.data;

        addToast({
          position: 'top-right',
          status: 'error',
          title: 'Erro na atualização',
          description:
            error || 'Ocorreu um erro ao atualizar a demanda, tente novamente.',
          isClosable: true,
        });
      } finally {
        setLoadingEdit(false);
      }
    },
    [addToast, onCloseEdit, demandEdit, handleFormatDepartment],
  );

  const handleOpenModalDelete = useCallback(
    (demId: number) => {
      setDemandDelete(demId);
      onOpenDelete();
    },
    [onOpenDelete],
  );

  const handleSubmitDelete = useCallback(() => {
    if (demand) {
      onCloseDelete();
    }

    api
      .delete(`demand/${demandDelete}`)
      .then(() => {
        const filteredDemand = demand.data.filter((deman) => {
          return deman.id !== demandDelete;
        });
        if (!filteredDemand.length) {
          setPage((state) => state - 1);
        }
        setDemand((state) => {
          return {
            ...state,
            data: filteredDemand,
            total: state.total - 1,
            to: state.to - 1,
          };
        });
        addToast({
          position: 'top-right',
          isClosable: true,
          status: 'success',
          title: 'Demanda deletada!',
          description: 'Demanda deletada com sucesso!',
        });

        setModalSuccess(modalSuccess + 1);
      })
      .catch((err) => {
        addToast({
          duration: 10000,
          position: 'top-right',
          isClosable: true,
          status: 'error',
          title: 'Demanda não deletada!',
          description:
            err.response.data?.error || 'Não foi possível apagar a demanda',
        });
      });
  }, [addToast, modalSuccess, onCloseDelete, demand, demandDelete]);

  const handleCreateDemand = useCallback(
    async (data) => {
      setLoadingCreate(true);
      try {
        formRef.current?.setErrors({});

        const validation = Yup.object().shape({
          name: Yup.string().required('Nome da demanda é obrigatório'),
          status_id: Yup.number().required('Status é obrigatório'),
        });

        await validation.validate(data, {
          abortEarly: false,
        });

        const response = await api.post<DemandList>('demand', data);
        const { data: newDemand } = response;

        addToast({
          position: 'top-right',
          status: 'success',
          title: 'Demanda cadastrada!',
          description: 'A demanda foi cadastrada com sucesso.',
          isClosable: true,
        });

        // Redefine formulário
        formRef.current?.reset();

        // Como a demanda foi criada agora não é preciso adicionar duração de atividades, já que ainda não possui
        setDemand((state) => {
          return {
            ...state,
            total: state.total + 1,
            to: state.to + 1,
            data: [
              {
                ...newDemand,
                total_duration_activities: '00h00',
                departments_names: handleFormatDepartment(newDemand),
              },
              ...state.data,
            ],
          };
        });
        onClose();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          position: 'top-right',
          status: 'error',
          title: 'Erro na atualização',
          description:
            err.response?.data?.error ||
            'Ocorreu um erro ao cadastrar a demanda',
          isClosable: true,
        });
      } finally {
        setLoadingCreate(false);
      }
    },
    [addToast, onClose, handleFormatDepartment],
  );

  return (
    <Box pos="relative" w="full">
      <Container>
        <SectionHeader
          title="Controle de Demandas"
          /* loading={!loaded} */
          pagename="Demandas"
        >
          <Button
            colorScheme="blue"
            isLoading={!loaded}
            isDisabled={!loaded}
            onClick={onOpen}
            ref={btnCreateRef}
          >
            <FiPlus />
            Cadastrar
          </Button>
        </SectionHeader>
      </Container>
      {!loaded && (
        <Box
          pos="absolute"
          top={0}
          left={0}
          w="full"
          h="500px"
          bg="rgba(255,255,255,0.5)"
        >
          <Loading />
        </Box>
      )}
      {!!demand?.data?.length && loaded && (
        <Table
          theadData={tableTitles}
          pagination={{
            current_page: demand.current_page,
            last_page: demand.last_page,
            per_page: demand.per_page,
            to: demand.to,
            total: demand.total,
          }}
          newPage={(pg) => setPage(pg)}
        >
          {demand.data?.map((dem) => (
            <Box as="tr" key={dem.id} _hover={{ bg: 'gray.50' }}>
              <Box as="td" py={3}>
                {dem.name}
              </Box>
              <Box as="td" py={3} textAlign="center">
                {dem.total_duration_activities}
              </Box>
              <Box as="td" py={3}>
                {dem.description}
              </Box>
              <Box as="td" py={3}>
                {dem?.departments_names}
              </Box>
              <Box as="td" py={3}>
                {dem.permission?.id === 1 ? (
                  // Apenas gerentes podem planejar
                  <Flex justifyContent="center">
                    <Tooltip label={dem.permission?.description}>
                      <Box>
                        <FaUserTie />
                      </Box>
                    </Tooltip>
                  </Flex>
                ) : (
                  // Funcionarios também podem planejar
                  <Flex justifyContent="center">
                    <Tooltip label={dem.permission?.description}>
                      <Box>
                        <FaUserFriends />
                      </Box>
                    </Tooltip>
                  </Flex>
                )}
              </Box>
              <Box as="td" py={3}>
                <Box>
                  <BadgeStatusInfo
                    status={dem.status.name}
                    statusColor={dem.status.color.name}
                  />
                </Box>
              </Box>
              <Box as="td" py={3} className="row">
                <Flex justifyContent="flex-end">
                  <IconButton
                    aria-label="Visualizar"
                    icon={<FiEye />}
                    variant="ghost"
                    bg="blue.50"
                    ml={[0, 2]}
                    colorScheme="blue"
                    onClick={() => handleDemandActivitiesList(dem.id)}
                  />
                  <IconButton
                    aria-label="Editar"
                    icon={<FiEdit />}
                    variant="ghost"
                    bg="yellow.50"
                    ml={[0, 2]}
                    colorScheme="yellow"
                    onClick={() => handleEditDemand(dem)}
                  />
                  <IconButton
                    aria-label="Deletar"
                    icon={<FiTrash />}
                    variant="ghost"
                    bg="red.50"
                    ml={[0, 2]}
                    colorScheme="red"
                    onClick={() => handleOpenModalDelete(dem.id)}
                  />
                </Flex>
              </Box>
            </Box>
          ))}
        </Table>
      )}

      {loaded && !demand?.data?.length && (
        <Alert status="info" variant="left-accent" mx={-4} my={4}>
          <AlertIcon />
          Ainda não há nenhuma demanda cadastrada
        </Alert>
      )}

      <ModalChakra
        title="Atualizar Demanda"
        onClose={() => {
          setDemandEdit(null);
          onCloseEdit();
        }}
        isOpen={isOpenEdit}
        onSubmit={() => formRefEdit.current?.submitForm()}
        isLoading={loadingEdit}
      >
        <Form
          initialData={demandEdit || undefined}
          ref={formRefEdit}
          onSubmit={handleEditDemandForm}
        >
          <FormControl>
            <Input
              icon={FiMail}
              label="Nome"
              name="name"
              type="text"
              placeholder="Digite o nome da demanda"
              autoFocus
              toUpperCase
            />

            <Box>
              <Box>
                <Flex alignItems="center">
                  <FormLabel htmlFor="department">
                    Associar algum departamento?
                  </FormLabel>
                  <Switch
                    id="department"
                    onChange={() =>
                      setTogglEditDepartment(!toggleEditDepartment)
                    }
                    defaultIsChecked={!!demandEdit?.departments?.length}
                    size="sm"
                    mb={1}
                  />
                </Flex>
                {toggleEditDepartment && (
                  <CheckboxInput
                    name="department_id"
                    options={departments?.map((dep) => {
                      return {
                        ...dep,
                        isChecked: !!demandEdit?.departments.find(
                          (depEdit) => Number(depEdit.id) === Number(dep.id),
                        ),
                      };
                    })}
                  />
                )}
              </Box>
            </Box>

            <SelectChakra label="Status" options={statusDem} name="status_id" />

            <SelectChakra
              label="Permissão"
              options={permissionsDem}
              name="permission_id"
            />

            <TextareaChakra
              label="Descrição"
              placeholder="Descrição da Demanda"
              name="description"
            />
          </FormControl>
        </Form>
      </ModalChakra>
      <AlertDialog
        title="Deletar Demanda?"
        description="Tem certeza que deseja deletar a demanda selecionada"
        isOpen={isOpenDelete}
        leastDestructiveRef={cancelDeleteRef}
        onClose={onCloseDelete}
        onSubmit={handleSubmitDelete}
      />

      {loaded && (
        <Drawer
          preserveScrollBarGap
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          finalFocusRef={btnCreateRef}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              Cadastrar Demanda
            </DrawerHeader>

            <DrawerBody overflowY="auto">
              <Box minH={200}>
                <Form ref={formRef} onSubmit={handleCreateDemand}>
                  <Stack>
                    <InputGroup mb={0}>
                      <Input
                        icon={FiMail}
                        label="Nome"
                        name="name"
                        type="text"
                        placeholder="Digite o nome da demanda"
                        autoFocus
                        toUpperCase
                      />
                    </InputGroup>

                    <Box>
                      <Box>
                        <Flex alignItems="center">
                          <FormLabel htmlFor="department">
                            Associar a algum departamento?
                          </FormLabel>
                          <Switch
                            id="department"
                            onChange={() =>
                              setToggleDepartment(!toggleDepartment)
                            }
                            defaultIsChecked={toggleDepartment}
                            size="sm"
                            mb={1}
                          />
                        </Flex>
                        {toggleDepartment && (
                          <CheckboxInput
                            name="department_id"
                            options={departments}
                          />
                        )}
                      </Box>
                    </Box>

                    <SelectChakra
                      options={statusDem}
                      name="status_id"
                      label="Status"
                    />

                    <SelectChakra
                      label="Permissão"
                      options={permissionsDem}
                      name="permission_id"
                    />

                    <TextareaChakra
                      label="Descrição"
                      placeholder="Descrição da Demanda"
                      name="description"
                    />
                  </Stack>
                </Form>
              </Box>
            </DrawerBody>

            <DrawerFooter justifyContent="flex-end">
              {loaded && (
                <Button
                  variant="outline"
                  mr={3}
                  onClick={() => onClose()}
                  size="lg"
                >
                  Fechar
                </Button>
              )}

              <Button
                variant="solid"
                colorScheme="blue"
                isLoading={loadingCreate}
                /* isDisabled={!loadingCreate} */
                size="lg"
                type="submit"
                ml="auto"
                onClick={() => formRef.current?.submitForm()}
              >
                Cadastrar
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
};

export default DemandActivities;
